import React, { FC, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { Button } from "../../../components/Button/Button";
import { useUser } from "../../../hooks/useUser";
import { useProcessSiblings } from "../../../hooks/useProcessSiblings";
import { isInternal } from "../../../utils";
import { BrandProject } from "./constants";
import { BRAND_PROJECT_STATUSES, describeSharedSiblings } from "../helper";
import {
    projectStatusErrorMessage,
    restoreBlockedReason,
    restoreProject,
    restoreTargetFor
} from "./projectStatusActions";

interface Props {
    project: BrandProject;
    onClose: () => void;
    /** Fired after a successful restore. */
    onRestored: () => void;
}

const Row: FC<{ label: string; value?: string | null }> = ({ label, value }) => {
    if (!value) return null;
    return (
        <Box className="flex gap-4 items-start">
            <Typography className="!text-text-2 !font-circular !text-[14px] min-w-[110px] shrink-0">
                {label}
            </Typography>
            <Typography className="!text-text-1 !font-circular !text-[14px] !font-medium whitespace-pre-wrap">
                {value}
            </Typography>
        </Box>
    );
};

/**
 * What a Cancelled project's status chip opens: the reason it was cancelled,
 * the status it held at the time, and — for internal users — a Restore Project
 * action. Same shape as the Brief Denied chip, which shows the denial reason
 * with an Edit Brief action.
 *
 * Where a restore LANDS is the server's call (restoreProject): a project that
 * shares its selection with live siblings rejoins them at the most advanced
 * sibling's status; a project alone on its selection goes back to
 * pre_cancel_status. The dialog previews that using the same sibling data the
 * approval modal fetches, and reports what the server actually did.
 */
export const CancelledProjectDialog: FC<Props> = ({ project, onClose, onRestored }) => {
    const { role } = useUser();
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const internal = isInternal(role);

    const sharedProcess = useProcessSiblings(
        project?.order_id != null ? Number(project.order_id) : undefined,
        project?.id,
        { siblingCount: project?.process_project_count }
    );
    const liveSiblings = sharedProcess.siblingProjects.filter(
        (p) => Number(p.id) !== Number(project?.id) && p.status !== BRAND_PROJECT_STATUSES.CANCELLED
    );
    // Three states, not two: still loading (wait for it), fetch FAILED (the
    // siblings are unknown — the server still knows them, so never treat this
    // as "no siblings" and never block on the missing pre_cancel_status), or
    // settled (including the skipped 1:1 case, which settles at once with none).
    const siblingsLoading = sharedProcess.isLoading;
    const siblingsFailed = !siblingsLoading && !!sharedProcess.error;
    const siblingsKnown = !siblingsLoading && !siblingsFailed;
    const target = siblingsKnown ? restoreTargetFor(project, sharedProcess.siblingProjects) : null;
    const blocked = restoreBlockedReason(project, siblingsKnown ? { hasLiveSiblings: liveSiblings.length > 0 } : {});

    const handleRestore = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const updated = await restoreProject(project);
            toast.success(`Project restored to ${updated.status || target?.status || "its previous status"}.`);
            // onRestored unmounts this dialog, so nothing may touch state after it.
            onRestored();
            return;
        } catch (err) {
            toast.error(projectStatusErrorMessage(err));
        }
        setSubmitting(false);
    };

    let restoreLabel = "Restore Project";
    if (submitting) restoreLabel = "Restoring...";
    else if (confirming) restoreLabel = "Yes, restore project";

    const landingCopy = (() => {
        if (siblingsFailed) {
            return (
                <>
                    Couldn&apos;t check whether other projects share this selection. If they do, it will
                    rejoin them where they are; otherwise it goes back to{" "}
                    <b className="text-text-1">{project.pre_cancel_status || "its previous status"}</b>.
                </>
            );
        }
        if (!target || !target.status) return null;
        if (target.rejoins) {
            return (
                <>
                    It will rejoin its shared expert selection at{" "}
                    <b className="text-text-1">{target.status}</b>, alongside{" "}
                    {describeSharedSiblings(liveSiblings)}.
                </>
            );
        }
        return (
            <>
                It will go back to <b className="text-text-1">{target.status}</b>.
            </>
        );
    })();

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h5" className="!text-[20px]">
                    {confirming ? "Restore this project?" : "Project Cancelled"}
                </Typography>
            </DialogTitle>
            <DialogContent className="border-y border-border-token">
                <Box className="flex flex-col gap-3 py-4">
                    {confirming ? (
                        <Typography className="!text-text-2 !font-circular !text-[16px] !leading-[24px]">
                            <b className="text-text-1">{project.title}</b> will be restored and the brand will
                            be notified. {landingCopy} The cancellation reason is cleared.
                        </Typography>
                    ) : (
                        <>
                            <Row label="Project" value={project.title} />
                            <Row label="Reason" value={project.cancellation_reason || "No reason was recorded."} />
                            <Row label="Was at" value={project.pre_cancel_status} />
                            {internal && !blocked && landingCopy && (
                                <Typography className="!text-text-2 !font-circular !text-[14px] pt-2">
                                    {landingCopy}
                                </Typography>
                            )}
                            {internal && !blocked && siblingsLoading && (
                                <Typography className="!text-text-2 !font-circular !text-[14px] pt-2">
                                    Checking whether other projects share this selection…
                                </Typography>
                            )}
                            {internal && blocked && (
                                <Typography className="!text-text-2 !font-circular !text-[14px] pt-2">
                                    {blocked}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions className="!px-6 !py-4">
                <Box className="flex items-center gap-6">
                    <Button
                        variant="default"
                        disabled={submitting}
                        onClick={confirming ? () => setConfirming(false) : onClose}
                        className="!bg-surface-2 hover:!bg-track !text-text-1 h-[40px] !py-0 !px-4 !min-w-fit"
                    >
                        {confirming ? "Back" : "Close"}
                    </Button>
                    {internal && !blocked && (
                        <Button
                            variant="contained"
                            // Wait for the sibling check: the confirm copy must say
                            // where the project will land before the user agrees.
                            disabled={submitting || siblingsLoading}
                            onClick={confirming ? handleRestore : () => setConfirming(true)}
                            className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#1FCE7A] hover:!bg-[#45ae7b]"
                        >
                            {restoreLabel}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};
